/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import { action, observable } from 'mobx';

class AppConfig {
  /**
   * Flag to indicate whether to show download error screen
   */
  @observable
  showDownloadError = false;

  /**
   * Flag to indicate whether app panel is masked
   */
  @observable
  isMasked = false;

  /**
   * Flag to indicate whether downloads are possible on this platform or not
   *
   * Our desktop-only product do not work on a touch device,
   * so this flag will be true in this case.
   *
   * Some Windows devices are touch devices, but
   * can still run our products, so this flag will be false in this case.
   *
   * However, Windows RT and ChromeOS devices are ARM-based and
   * cannot run our products, so this flag will be true in this case.
   */
  isNonDownloadableSurface;

  /**
   * For identifying the TestB experience,
   * to display dynamic banner
   */
  shortHeight;

  /**
   * For identifying the TestA experience,
   * to not display the banner
   */
  noBanner;

  /**
   * Dialog manager for specifying queue for dialogs,
   * if not specified, a new one is created
   */
  dialogManager;

  /**
   * Object of layouts
   */
  @observable
  layouts;

  /**
   * Flag used for checking wheather no Internet screen is to be shown(Getting used in CCD)
   */
  @observable
  isOnline

  /**
   * Callback for reloading apps panel
   */
  appReloadCallback;

  /**
   * Callback for relaunching apps panel
   */
  relaunchCallback;

  /**
   * Callback triggered when pdp is loaded for any product
   */
  getPDPDataCallback;

  /**
   * BasePath(fileSystemPath) for cached pdp assets(CCD Only)
   */
   pdpAssetsBasePath;

  /**
   * Callback for analytic events
   */
  analyticsCallback = () => {};

  /**
   * Callback to open links
   */
  linkCallbacks = {};


  cssWrapperClass = '';

  proceedAsAdminCallback;

  /**
   * If true, secondary CTAs will be moved to
   * dropdown above MAX_BUTTON_LIST_TEXT_WIDTH_S.
   * This is added for handling certain locales
   * in which overflow happens
   */
  shouldWrapAtLowerBP = false;

  @action
  init({
      showDownloadError = false,
      isMasked = false,
      isNonDownloadableSurface = false,
      shortHeight = false,
      noBanner = false,
      dialogManager,
      layouts,
      getPDPDataCallback,
      analyticsCallback,
      appReloadCallback,
      linkCallbacks = {},
      proceedAsAdminCallback,
      relaunchCallback,
      pdpAssetsBasePath,
      isOnline = true,
      cssWrapperClass = '',
      shouldWrapAtLowerBP = false,
  }) {
      this.isMasked = isMasked;
      this.isNonDownloadableSurface = isNonDownloadableSurface;
      this.shortHeight = shortHeight;
      this.noBanner = noBanner;
      this.dialogManager = dialogManager;
      this.layouts = layouts;
      this.getPDPDataCallback = getPDPDataCallback;
      this.analyticsCallback = analyticsCallback;
      this.showDownloadError = showDownloadError;
      this.appReloadCallback = appReloadCallback;
      this.proceedAsAdminCallback = proceedAsAdminCallback;
      this.linkCallbacks = linkCallbacks;
      this.relaunchCallback = relaunchCallback;
      this.pdpAssetsBasePath = pdpAssetsBasePath;
      this.isOnline = isOnline;
      this.cssWrapperClass = cssWrapperClass;
      this.shouldWrapAtLowerBP = shouldWrapAtLowerBP;
  }

  @action
  setDownloadError(showDownloadError) {
      this.showDownloadError = showDownloadError;
  }

  @action
  setMasked(isMasked) {
      this.isMasked = isMasked;
  }

  @action
  setLayouts(layouts) {
      this.layouts = layouts;
  }

  @action
  setOnline(isOnline) {
      this.isOnline = isOnline;
  }

  @action
  setPdpAssetsBasePath(basePath) {
      this.pdpAssetsBasePath = basePath;
  }
}

export default new AppConfig();
